/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useCallback, useEffect, useState } from 'react';
import Modal from 'react-modal';
import { Grid, makeStyles } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import PopupTopBar from './PopupTopBar';
import PopupFooter from './PopupFooter';
import CustomPopupText from '../../shared/CustomPopupText';
import CustomTextField from '../../shared/CustomTextField';
import CustomUploadField from '../../shared/CustomUploadField';
import CustomDropDown from '../../shared/CustomDropDown';
import RnpPrimaryColorSelect from './RnpPrimaryColorSelect';
import RnpScreenPreview from './RnpScreenPreview';
import { showProgress } from '../../../redux/actions/DealershipActions';
import service from '../../../apis/service';
import { ErrorToast, notifySuccess } from '../../utils/Utils';
import {
  convertToFormData,
  normalizeGradientVariant,
  normalizeHexColor,
  unwrapRnpPartnerDetail,
  validateForm,
} from '../utils';
import { apiEndpoints } from '../../../constants/apiEndpoints';
import { DIMENSIONS } from '../../../constants/aspectRatio';

const createCustomStyles = (height) => ({
  content: {
    height: `${height}px`,
    width: '527px',
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    padding: '0px',
    background: '#F2F4F7',
  },
});

const RnpAddNewPopup = ({ close, add, title, id, fetchData }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const rnpList = useSelector((state) => state.rnpData.rnpList);
  const selectedType = useSelector((state) => state.rnpData.selectedType);
  const [rnpTypeList, setRnpTypeList] = useState([]);
  const [data, setData] = useState({});
  const [logoImg, setLogoImg] = useState('');
  const [iconImg, setIconImg] = useState('');

  const getDetails = useCallback(async () => {
    if (id == null || id === '') return;
    dispatch(showProgress(true));
    try {
      const res = await service.get(apiEndpoints.GET_RNP_DETAILS.replace('ID', id));
      const row = unwrapRnpPartnerDetail(res);
      if (!row) {
        ErrorToast('Unable to load RNP details');
        return;
      }
      const bgRaw = row?.backgroundColor != null ? String(row.backgroundColor) : '';
      setData({
        name: row.name ?? '',
        displayName: row.displayName ?? '',
        type: row.type ?? selectedType,
        logo: row.logo,
        icon: row.icon,
        backgroundColor: normalizeHexColor(bgRaw) || '',
        gradientVariant: normalizeGradientVariant(row.gradientVariant ?? row.gradientVarient),
      });
    } catch (error) {
      ErrorToast(error);
    } finally {
      dispatch(showProgress(false));
    }
  }, [dispatch, id, selectedType]);

  useEffect(() => {
    if (!add) {
      getDetails();
    }
  }, [add, getDetails]);

  useEffect(() => {
    const typeList = rnpList?.map((rnp) => ({ id: rnp?.type, name: rnp?.type }));
    setRnpTypeList(typeList);
  }, [rnpList]);

  useEffect(() => {
    if (add) {
      setData((prev) => ({ ...prev, type: selectedType }));
    }
  }, [add, selectedType]);

  const cancelBtn = () => close();
  const saveRnp = async () => {
    const check = validateForm(data, add);
    if (!check) return;
    dispatch(showProgress(false));
    try {
      const formData = convertToFormData(data, add);
      dispatch(showProgress(true));
      if (add) {
        await service.post(apiEndpoints.CREATE_RNP, formData);
        notifySuccess('RNP Successfully created');
      } else {
        await service.put(apiEndpoints.UPDATE_RNP_DETAILS.replace('ID', id), formData);
        notifySuccess('RNP Successfully updated');
      }
      close(true);
      dispatch(showProgress(false));
      fetchData(selectedType);
    } catch (error) {
      ErrorToast(error);
      dispatch(showProgress(false));
    }
  };
  const handleChange = ({ target: { value, name } }) => {
    setData((prev) => ({ ...prev, [name]: value }));
  };
  const handleImageUploadNew = ({ target: { files, name } }) => {
    const filesData = files[0];
    if (!filesData || (name !== 'logo' && name !== 'icon')) return;

    const reader = new global.FileReader();
    reader.onload = (ev) => {
      if (name === 'logo') {
        setLogoImg(ev.target.result);
      } else {
        setIconImg(ev.target.result);
      }
    };
    reader.readAsDataURL(filesData);

    setData((prev) => ({ ...prev, [name]: filesData }));
  };
  return (
    <Modal
      ariaHideApp={false}
      isOpen
      onRequestClose={() => false}
      shouldCloseOnEsc={false}
      style={createCustomStyles(720)}
    >
      <div className={classes.modalRoot}>
        <PopupTopBar add={add} title={title} close={close} />
        <Grid container direction="column" wrap="nowrap" className={classes.contentContainer}>
          <Grid item xs={12}>
            <CustomPopupText text="RNP Name * (to be used on the Explore Screen)" required />
          </Grid>
          <Grid item xs={12}>
            <CustomTextField name="name" handleChange={handleChange} value={data?.name ?? ''} width="100%" />
          </Grid>
          <Grid item xs={12}>
            <CustomPopupText text="RNP Display Name * (to be used on the Home Screen section)" required />
          </Grid>
          <Grid item xs={12}>
            <CustomTextField
              name="displayName"
              handleChange={handleChange}
              value={data?.displayName ?? ''}
              width="100%"
            />
          </Grid>
          <Grid item xs={12}>
            <CustomPopupText text="RNP Type" required />
          </Grid>
          <Grid item xs={12}>
            <CustomDropDown
              width="100%"
              value={selectedType || 'Select a value'}
              name="type"
              list={rnpTypeList}
              handleChange={handleChange}
              disabled
            />
          </Grid>
          <Grid item xs={12}>
            <CustomPopupText text="RNP Logo * (to be displayed on the certificate - max size - 500kb)" required />
          </Grid>
          <Grid item xs={12}>
            <CustomUploadField
              type="image"
              name="logo"
              imgSrc={logoImg || data?.logo}
              handleChange={handleImageUploadNew}
              customStyles={{ height: '44px', width: '100%' }}
              dimensions={DIMENSIONS.rnpLogo}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomPopupText text="RNP Icon * (to be displayed on the Explore Screen - max size - 500kb)" required />
          </Grid>
          <Grid item xs={12}>
            <CustomUploadField
              type="image"
              name="icon"
              imgSrc={iconImg || data?.icon}
              handleChange={handleImageUploadNew}
              customStyles={{ height: '44px', width: '100%' }}
              dimensions={DIMENSIONS.rnpIcon}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomPopupText text="RNP Background Color" required />
          </Grid>
          <Grid item xs={12}>
            <RnpPrimaryColorSelect
              value={data?.backgroundColor}
              onChange={(hex) => setData((prev) => ({ ...prev, backgroundColor: hex }))}
            />
          </Grid>
          <Grid item xs={12}>
            <RnpScreenPreview
              backgroundColor={data?.backgroundColor}
              partnerName={data?.name}
              iconSrc={iconImg || (typeof data?.icon === 'string' ? data.icon : '')}
              gradientVariant={data?.gradientVariant}
              onGradientChange={(v) => setData((prev) => ({ ...prev, gradientVariant: v }))}
            />
          </Grid>
        </Grid>
        <PopupFooter cancelBtn={cancelBtn} save={saveRnp} add={add} />
      </div>
    </Modal>
  );
};

const useStyles = makeStyles({
  modalRoot: {
    position: 'relative',
    height: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
  },
  contentContainer: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    padding: '8px 16px 88px',
    overflowY: 'auto',
    boxSizing: 'border-box',
    '& > .MuiGrid-item': {
      width: '100%',
      maxWidth: '100%',
    },
    '& .MuiTextField-root': {
      width: '100%',
      maxWidth: '100%',
    },
    '& .MuiFormControl-root': {
      width: '100%',
      maxWidth: '100%',
    },
  },
});
export default RnpAddNewPopup;
